// frontend/src/utils/httmScheduleGenerator.ts
import { addDays, parse } from 'date-fns';
import type { HttmConfig, HttmEvent } from '@/types/httmSchedule';
import {
  calculateH2o2Dose,
  calculatePraziproDose,
  formatVolume,
} from '@/types/httmSchedule';

/**
 * Generates the full HTTM (Hybrid Tank Transfer Method) quarantine schedule.
 *
 * Protocol overview:
 * - Fish alternate between Tank 1 and Tank 2 every 72 hours
 * - Praziquantel is dosed 48 hours after entering a fresh tank (rounds 1 & 3)
 * - H2O2 baths occur at Day 1 (optional), Day 7, and Day 13 (conditional)
 * - If H2O2 bath is done at Day 1, it can be skipped at Day 13
 */
export function generateHttmSchedule(config: HttmConfig): HttmEvent[] {
  const start = parse(
    `${config.startDate} ${config.startTime}`,
    'yyyy-MM-dd HH:mm',
    new Date()
  );

  const h2o2Dose = calculateH2o2Dose(
    config.bathVolumeLiters,
    config.h2o2Concentration
  );
  const praziDose = calculatePraziproDose(config.qtTankSizeLiters);
  const bathVol = formatVolume(config.bathVolumeLiters, config.units);
  const h2o2DoseStr = `${h2o2Dose.toFixed(1)} ml of ${config.h2o2Concentration}% H2O2 into ${bathVol} saltwater`;
  const praziDoseStr = `${praziDose.toFixed(1)} ml PraziPro for ${formatVolume(config.qtTankSizeLiters, config.units)} tank`;

  const events: HttmEvent[] = [];

  // ── Day 1: Optional H2O2 bath ──
  if (config.includeH2o2 && config.initialH2o2Bath) {
    events.push({
      day: 1,
      date: start,
      action: 'H2O2 Bath (30 min)',
      timing: 'Right before QT start',
      notes: h2o2DoseStr,
      category: 'h2o2',
      tank: 'H2O2 Bath',
      skipped: false,
    });
  }

  // ── Day 1: Fish into Tank 1 ──
  events.push({
    day: 1,
    date: start,
    action: 'Fish into Tank 1',
    timing: 'QT clock starts',
    notes: 'Begin quarantine - ensure Tank 1 is prepared with matched salinity and temperature',
    category: 'transfer',
    tank: 'Tank 1',
    skipped: false,
  });

  // ── Day 3: Dose Prazi (48 hours after Day 1) ──
  if (config.includePrazi) {
    events.push({
      day: 3,
      date: addDays(start, 2),
      action: 'Dose Praziquantel',
      timing: 'After 48 hours in Tank 1',
      notes: praziDoseStr,
      category: 'prazi',
      tank: 'Tank 1',
      skipped: false,
    });
  }

  // ── Day 4: Transfer to Tank 2 (72 hours from Day 1) ──
  events.push({
    day: 4,
    date: addDays(start, 3),
    action: 'Transfer to Tank 2',
    timing: 'Before 72 hours in Tank 1',
    notes: 'Clean and refill Tank 1 after transfer. Discard airstone.',
    category: 'transfer',
    tank: 'Tank 2',
    skipped: false,
  });

  // ── Day 7: H2O2 bath + Transfer to Tank 1 (72 hours from Day 4) ──
  if (config.includeH2o2) {
    events.push({
      day: 7,
      date: addDays(start, 6),
      action: 'H2O2 Bath (30 min)',
      timing: 'Before 72 hours in Tank 2',
      notes: h2o2DoseStr,
      category: 'h2o2',
      tank: 'H2O2 Bath',
      skipped: false,
    });
  }

  events.push({
    day: 7,
    date: addDays(start, 6),
    action: 'Transfer to Tank 1',
    timing: 'After H2O2 bath',
    notes: 'Clean and refill Tank 2 after transfer. Discard airstone.',
    category: 'transfer',
    tank: 'Tank 1',
    skipped: false,
  });

  // ── Day 9: Dose Prazi (48 hours after Day 7) ──
  if (config.includePrazi) {
    events.push({
      day: 9,
      date: addDays(start, 8),
      action: 'Dose Praziquantel',
      timing: 'After 48 hours in Tank 1',
      notes: `${praziDoseStr} (2nd round)`,
      category: 'prazi',
      tank: 'Tank 1',
      skipped: false,
    });
  }

  // ── Day 10: Transfer to Tank 2 (72 hours from Day 7) ──
  events.push({
    day: 10,
    date: addDays(start, 9),
    action: 'Transfer to Tank 2',
    timing: 'Before 72 hours in Tank 1',
    notes: 'Clean and refill Tank 1 after transfer. Discard airstone.',
    category: 'transfer',
    tank: 'Tank 2',
    skipped: false,
  });

  // ── Day 13: Conditional H2O2 + Transfer to Tank 1 + Observe ──
  if (config.includeH2o2) {
    events.push({
      day: 13,
      date: addDays(start, 12),
      action: 'H2O2 Bath (30 min)',
      timing: 'Before 72 hours in Tank 2',
      notes: config.initialH2o2Bath
        ? 'Not needed - initial H2O2 bath was done on Day 1'
        : h2o2DoseStr,
      category: 'h2o2',
      tank: 'H2O2 Bath',
      skipped: config.initialH2o2Bath,
    });
  }

  events.push({
    day: 13,
    date: addDays(start, 12),
    action: 'Transfer to Tank 1 & Begin Observation',
    timing: 'Before 72 hours in Tank 2',
    notes: 'Fish is ready for display tank - observe for any signs of disease',
    category: 'observation',
    tank: 'Tank 1',
    skipped: false,
  });

  // ── Day 14+: Into Display Tank ──
  events.push({
    day: 14,
    date: addDays(start, 13),
    action: 'Move to Display Tank',
    timing: 'Whenever ready',
    notes: 'Quarantine complete - acclimate to display tank parameters',
    category: 'observation',
    tank: 'Display Tank',
    skipped: false,
  });

  return events;
}
